import { ScenarioNode } from "@features/builder/graph/types"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { GraphDetails } from "@features/builder/types"
import MnemonicTable from "@features/graph/details/components/MnemonicTable"
import {
  getScenarioColumnIndex,
  getUniqueScenarios,
} from "@features/graph/details/components/MnemonicTable/helpers"
import ScenariosTabs from "@features/graph/details/components/MnemonicTable/ScenariosTabs"
import { CircularProgress, FormHelperText, Grid, Typography } from "@mui/material"
import {
  useGetMnemonisDataQuery,
  useGetScenarioSourceMnemonicsQuery,
} from "@store/services/graphsService"
import { useEffect, useMemo, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { getNormalisedGraphKey } from "@utils/graph"
import { MissingScenarioFileCta } from "@features/builder/NodeDrawer/NodeForm/MissingScenarioFileCta"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import ScenarioMnemonicField from "@components/ScenarioMnemonicField"

type ScenarioAccordionProps = {
  node: ScenarioNode
}

const ScenarioAccordion = (props: ScenarioAccordionProps) => {
  const [selectedScenarioTab, setSelectedScenarioTab] = useState("")
  const form = useFormContext<NodeFormValues>()
  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = JSON.parse(graphDocumentData.data)
  const scenarioFilePath = graphData?.ScenarioSource
  const scenarioVersion = graphData?.ScenarioVersion
  const graphKey = graphData ? getNormalisedGraphKey(graphData) : ""

  // Track previous scenarioVersion to detect actual changes
  const prevScenarioVersionRef = useRef<string | null | undefined>()

  const scenarioValue = form.watch("scenario_mnemonic")

  const getMnemonicsQuery = useGetScenarioSourceMnemonicsQuery(
    {
      graphKey,
      type: "scenario",
      // Include scenarioVersion to ensure query refetches when scenario file changes
      scenarioVersion: scenarioVersion ?? "",
    },
    {
      skip: !scenarioFilePath,
    },
  )

  const mnemonics = useMemo(
    () => (scenarioFilePath ? getMnemonicsQuery.data?.map(({ mnem }) => mnem) ?? [] : []),
    [getMnemonicsQuery.data, scenarioFilePath],
  )

  const shouldFetchMnemonicValues =
    !!scenarioFilePath && !!scenarioValue && mnemonics.includes(scenarioValue)

  const getMnemonicValuesQuery = useGetMnemonisDataQuery(
    {
      type: "scenario",
      graphKey,
      mnemonic: scenarioValue ?? "",
      scenarioVersion: scenarioVersion ?? "",
    },
    {
      skip: !shouldFetchMnemonicValues,
    },
  )

  // Handle scenario file changes:
  // Only run when scenarioVersion actually changes, not on every render or node switch
  useEffect(() => {
    // On first mount, initialize the ref without running logic
    if (prevScenarioVersionRef.current === undefined) {
      prevScenarioVersionRef.current = scenarioVersion
      return
    }

    // Check if scenarioVersion actually changed
    if (prevScenarioVersionRef.current === scenarioVersion) return

    // Wait for both queries to finish fetching before refetch to avoid race conditions
    if (getMnemonicsQuery.isFetching || getMnemonicValuesQuery.isFetching) return

    getMnemonicsQuery.refetch()

    if (shouldFetchMnemonicValues) {
      getMnemonicValuesQuery.refetch()
    }

    // Update ref for next comparison
    prevScenarioVersionRef.current = scenarioVersion
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch is a stable method reference
  }, [
    scenarioVersion,
    scenarioFilePath,
    getMnemonicsQuery.isFetching,
    getMnemonicValuesQuery.isFetching,
    shouldFetchMnemonicValues,
  ])

  const { getNodeValidation } = useNodesValidation()

  const mnemonicValuesData = shouldFetchMnemonicValues ? getMnemonicValuesQuery.data ?? null : null

  const scenariosTabs = useMemo(
    () => (mnemonicValuesData ? getUniqueScenarios(mnemonicValuesData) : []),
    [mnemonicValuesData],
  )

  useEffect(() => {
    setSelectedScenarioTab(scenariosTabs[0] ?? "")
  }, [scenariosTabs])

  const isFetching = getMnemonicsQuery.isFetching || getMnemonicValuesQuery.isFetching

  const filteredData = useMemo(() => {
    if (mnemonicValuesData && !mnemonicValuesData.empty) {
      // filter out rows that are not related to our selected tab
      const scenarioIndex = getScenarioColumnIndex(mnemonicValuesData)
      const mnemonicData = structuredClone(mnemonicValuesData)
      mnemonicData.rows = mnemonicData.rows.filter(
        (row) => row[scenarioIndex] === selectedScenarioTab,
      )

      return mnemonicData
    }

    return null
  }, [mnemonicValuesData, selectedScenarioTab])

  if (isFetching) {
    return (
      <NodeDrawerAccordion id="scenario" label="Scenario Data">
        <Grid item justifyContent="center" sx={{ display: "flex" }}>
          <CircularProgress />
        </Grid>
      </NodeDrawerAccordion>
    )
  }

  const errors = [getNodeValidation(props.node.id, "SCENARIO_MNEMONIC")?.errors].flatMap(
    (err) => err ?? [],
  )

  return (
    <NodeDrawerAccordion id="scenario" label="Scenario Data">
      <Grid item>
        <ScenarioMnemonicField
          control={form.control}
          name="scenario_mnemonic"
          label="Scenario Mnemonic"
        />
        {errors.map((err, idx) => (
          <FormHelperText key={idx} error>
            - {err.msg}
          </FormHelperText>
        ))}
        {!scenarioFilePath && <MissingScenarioFileCta />}
      </Grid>

      {scenarioValue && mnemonicValuesData && (
        <Grid item>
          <ScenariosTabs
            scenarios={scenariosTabs}
            onChange={setSelectedScenarioTab}
            selectedScenario={selectedScenarioTab}
          />
          {filteredData ? (
            <MnemonicTable data={filteredData} />
          ) : (
            <Typography>No data for selected scenario</Typography>
          )}
        </Grid>
      )}
    </NodeDrawerAccordion>
  )
}

export default ScenarioAccordion
